import { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { Button, Modal, TextInput } from "@dealer-center/core-components";
import { Locale } from "@dealer-center/core-labels";
import type { Fee, FeeType } from "@dealer-center/core-feemanager-interface";
import type { FeeWithUIState } from "../types";
import {
  parseCurrency,
  validateFeeAmount,
  validateFeeName
} from "../utils/format";

const FormRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-bottom: 16px;

  /**
   * MUI Override: Style Interstate TextInput labels to match design
   * Reason: Interstate TextInput does not expose variant or style props for labels.
   */
  .MuiFormLabel-root.MuiInputLabel-root {
    font-size: 12px !important;
    font-weight: 700 !important;
    text-transform: uppercase !important;
    color: #374151 !important;
  }
`;

const AmountRow = styled(FormRow)`
  max-width: 168px;

  @media (max-width: 480px) {
    max-width: 100%;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 16px;
  justify-content: flex-end;
  margin-top: 48px;
  margin-bottom: 24px;

  @media (max-width: 768px) {
    margin-top: 32px;
    gap: 12px;
  }

  @media (max-width: 480px) {
    margin-top: 24px;
    flex-direction: column;
    gap: 8px;
  }
`;

const StyledModal = styled(Modal)`
  .ids-modal-title {
    margin-bottom: 16px;
    padding: 24px 24px 0 24px !important;
  }

  @media (max-width: 768px) {
    .ids-modal-title {
      padding: 16px 16px 0 16px !important;
    }

    .ids-modal-content {
      padding: 16px !important;
    }
  }

  @media (max-width: 480px) {
    .ids-modal-title {
      padding: 12px 12px 0 12px !important;
    }

    .ids-modal-content {
      padding: 12px !important;
    }
  }
`;

// Add mode props
interface AddModeProps {
  mode: "add";
  activeTab: "NEW" | "USED";
  isOpen: boolean;
  onAdd: (data: { name: string; amount: number | null; type: FeeType }) => void;
  onCancel: () => void;
}

// Edit mode props
interface EditModeProps {
  mode: "edit";
  fee: FeeWithUIState;
  isOpen: boolean;
  onClose: () => void;
  onUpdate: (updates: Partial<Pick<Fee, "name" | "amount" | "type">>) => void;
}

export type AddFeeModalProps = AddModeProps | EditModeProps;

export const AddFeeModal = (props: AddFeeModalProps) => {
  const { isOpen, mode } = props;

  // Add mode state
  const [addName, setAddName] = useState("");
  const [addAmount, setAddAmount] = useState("");
  const [errors, setErrors] = useState<{ name?: string; amount?: string }>({});

  const nameInputRef = useRef<HTMLDivElement>(null);
  const isFrenchCanadian = Locale.getLocale() === "fr-CA";
  const currencyProps = isFrenchCanadian
    ? { inputSuffix: "$" }
    : { inputPrefix: "$" };

  // Initialise form state when modal opens
  useEffect(() => {
    if (!isOpen || mode !== "add") return;
    setAddName("");
    setAddAmount("");
    setErrors({});
    const timer = setTimeout(() => {
      const input = nameInputRef.current?.querySelector("input");
      if (input) {
        input.focus();
        input.select();
      }
    }, 100);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const handleAmountChange = (
    rawValue: string,
    setter: (v: string) => void
  ) => {
    const normalized = rawValue.replace(/,/g, ".");
    let filtered = normalized.replace(/[^0-9.]/g, "");
    const parts = filtered.split(".");
    if (parts.length > 2) {
      filtered = parts[0] + "." + parts.slice(1).join("");
    }
    setter(filtered);
  };

  const handleAmountKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>,
    currentAmount: string
  ) => {
    const allowedKeys = [
      "Backspace",
      "Delete",
      "Tab",
      "ArrowLeft",
      "ArrowRight",
      "ArrowUp",
      "ArrowDown"
    ];
    const isNumeric = /^[0-9]$/.test(e.key);
    const isDecimal = e.key === "." || e.key === ",";

    if (!allowedKeys.includes(e.key) && !isNumeric && !isDecimal) {
      e.preventDefault();
      return;
    }
    if (isDecimal && currentAmount.includes(".")) {
      e.preventDefault();
      return;
    }
    if (isNumeric && currentAmount.includes(".")) {
      const amountParts = currentAmount.split(".");
      if (amountParts[1] && amountParts[1].length >= 2) {
        e.preventDefault();
      }
    }
  };

  const validateAndSubmitAdd = () => {
    const newErrors: { name?: string; amount?: string } = {};

    if (!validateFeeName(addName)) {
      newErrors.name = String(Locale.getText("FEEMANAGER_FEENAME_ERROR"));
    }

    const parsedAmount = parseCurrency(addAmount);
    if (addAmount && !validateFeeAmount(parsedAmount)) {
      newErrors.amount = String(Locale.getText("FEEMANAGER_AMOUNT_ERROR"));
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    if (mode === "add") {
      props.onAdd({
        name: addName.trim(),
        amount: parsedAmount,
        type: props.activeTab
      });
    }
  };

  const handleModalHide = ((...args: unknown[]) => {
    const reason = args[1] as string | undefined;
    if (reason === "backdropClick") return;
    if (mode === "add") {
      props.onCancel();
    } else {
      props.onClose();
    }
  }) as () => void;

  const modalTitle =
    mode === "add"
      ? Locale.getText(
          props.activeTab === "USED"
            ? "FEEMANAGER_ADDFEE_MODAL_TITLE_PREOWNED"
            : "FEEMANAGER_ADDFEE_MODAL_TITLE_NEW"
        )
      : String(props.fee.name);

  // Derive display values for edit mode
  const editName = mode === "edit" ? props.fee.name : addName;
  const editAmount =
    mode === "edit" ? String(props.fee.amount ?? "") : addAmount;

  return (
    <StyledModal
      header={modalTitle}
      show={isOpen}
      size="large"
      onHide={handleModalHide}
    >
      <FormRow ref={nameInputRef}>
        <TextInput
          id="fee-name"
          label={String(Locale.getText("FEEMANAGER_NAME"))}
          name="fee-name"
          placeholder={String(Locale.getText("FEEMANAGER_FEENAME_PLACEHOLDER"))}
          value={editName}
          onChange={e => {
            if (mode === "add") {
              setAddName(e.target.value);
              if (errors.name)
                setErrors(prev => ({ ...prev, name: undefined }));
            } else {
              props.onUpdate({ name: e.target.value });
            }
          }}
        />
        {errors.name && (
          <span style={{ color: "red", fontSize: "12px" }}>{errors.name}</span>
        )}
      </FormRow>

      <AmountRow>
        <TextInput
          id="fee-amount"
          label={String(Locale.getText("FEEMANAGER_AMOUNT"))}
          name="fee-amount"
          placeholder={String(Locale.getText("FEEMANAGER_AMOUNT_PLACEHOLDER"))}
          value={editAmount}
          {...currencyProps}
          onChange={e => {
            if (mode === "add") {
              handleAmountChange(e.target.value, setAddAmount);
              if (errors.amount)
                setErrors(prev => ({ ...prev, amount: undefined }));
            } else {
              const normalized = e.target.value.replace(/,/g, ".");
              let filtered = normalized.replace(/[^0-9.]/g, "");
              const parts = filtered.split(".");
              if (parts.length > 2)
                filtered = parts[0] + "." + parts.slice(1).join("");
              props.onUpdate({ amount: parseCurrency(filtered) });
            }
          }}
          onKeyDown={e =>
            handleAmountKeyDown(
              e as React.KeyboardEvent<HTMLInputElement>,
              editAmount
            )
          }
        />
        {errors.amount && (
          <span style={{ color: "red", fontSize: "12px" }}>
            {errors.amount}
          </span>
        )}
      </AmountRow>

      <ButtonGroup>
        {mode === "add" ? (
          <>
            <Button
              analyticsConfiguration={{
                eventName: "",
                eventProperties: undefined
              }}
              buttonStyle="secondary"
              onClick={props.onCancel}
            >
              {String(Locale.getText("FEEMANAGER_CANCEL"))}
            </Button>
            <Button
              analyticsConfiguration={{
                eventName: "",
                eventProperties: undefined
              }}
              buttonStyle="primary"
              onClick={validateAndSubmitAdd}
            >
              {String(Locale.getText("FEEMANAGER_ADDFEE"))}
            </Button>
          </>
        ) : (
          <Button
            analyticsConfiguration={{
              eventName: "",
              eventProperties: undefined
            }}
            buttonStyle="secondary"
            onClick={props.onClose}
          >
            {String(Locale.getText("CLOSE"))}
          </Button>
        )}
      </ButtonGroup>
    </StyledModal>
  );
};
